import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ALL_SKILL_TYPES,
  PROMPT_SOURCE_TEXT,
  deriveActiveSkillsFromLessonTestSelection,
  granularAnswerTypesFromModes,
  isSpeechAnswerType,
  modeHasCompatibleGranular,
  unionAnswerTypesForPromptPool,
} from '@/utils/testPromptAnswerMatrix'
import type { AnswerFormatMode, PromptTypeKey } from '@/utils/testPromptAnswerMatrix'
import type { TestCompletionMode, TestSkillGoal } from './testSettingsTypes'
import type { AttributeConditionRow } from './wordAttributeFilters'
import type { TestCreateProfile } from './testCreateProfiles'

/**
 * Состояние настроек конструктора теста — общее для обеих форм.
 *
 * Держит ровно то, что редактируют секции, и производные от матрицы величины.
 * Всё остальное — шаблоны, участники, предпросмотр, отправка — остаётся у
 * страницы: у теста из урока и у самостоятельного теста это разные сценарии, и
 * складывать их в один хук значило бы получить третью копию логики вместо
 * общей.
 */
export function useTestSettingsForm(profile: TestCreateProfile) {
  const [enabledPromptTypes, setEnabledPromptTypes] = useState<PromptTypeKey[]>([PROMPT_SOURCE_TEXT])
  const [enabledAnswerFormatModes, setEnabledAnswerFormatModes] = useState<AnswerFormatMode[]>([])
  const [autoCheckEnabledRaw, setAutoCheckEnabled] = useState(true)

  // Инвариант, а не эффект: форма без выключателя обязана держать автопроверку
  // включённой всегда — шаблон с `auto_check_enabled: false` иначе выключил бы
  // её ученику навсегда, а вернуть её ему было бы нечем.
  const autoCheckEnabled = profile.canDisableAutoCheck ? autoCheckEnabledRaw : true

  const [tagIds, setTagIds] = useState<number[]>([])
  const [attributeConditions, setAttributeConditions] = useState<AttributeConditionRow[]>([])
  const [relaxFilters, setRelaxFilters] = useState(true)

  const [mode, setMode] = useState<TestCompletionMode>('classic')
  const [countNeed, setCountNeed] = useState<number | ''>('')
  const [durationMin, setDurationMin] = useState<number | ''>('')
  const [seriesTarget, setSeriesTarget] = useState<number | ''>('')

  const [allowHints, setAllowHints] = useState(false)
  const [allowTranscription, setAllowTranscription] = useState(false)
  const [excludeDuplicateWords, setExcludeDuplicateWords] = useState(false)

  /** Последнее отображение снять нельзя: без него тест не из чего собрать. */
  const togglePromptType = useCallback((promptType: PromptTypeKey) => {
    setEnabledPromptTypes((prev) => {
      if (prev.includes(promptType)) {
        if (prev.length <= 1) return prev
        return prev.filter((x) => x !== promptType)
      }
      return [...prev, promptType]
    })
  }, [])

  const compatibleAnswerTypes = useMemo(
    () => unionAnswerTypesForPromptPool(enabledPromptTypes as string[], [...ALL_SKILL_TYPES]),
    [enabledPromptTypes],
  )

  const granularEnabledAnswerTypes = useMemo(() => {
    const fromModes = granularAnswerTypesFromModes(enabledAnswerFormatModes, compatibleAnswerTypes)
    if (!autoCheckEnabled) {
      const speechGranular = compatibleAnswerTypes.filter((at) => isSpeechAnswerType(at))
      if (enabledAnswerFormatModes.length === 0) {
        return speechGranular
      }
      return fromModes.filter((at) => isSpeechAnswerType(at))
    }
    return fromModes
  }, [autoCheckEnabled, enabledAnswerFormatModes, compatibleAnswerTypes])

  // Смена отображения могла обнулить совместимость формата — снимаем те, что
  // матрица больше не разрешает, иначе в payload уедет невозможная пара.
  useEffect(() => {
    setEnabledAnswerFormatModes((prev) =>
      prev.filter((m) => modeHasCompatibleGranular(m, compatibleAnswerTypes)),
    )
  }, [compatibleAnswerTypes])

  // Без автопроверки остаётся только устный ответ: печать и выбор варианта
  // проверять некому.
  useEffect(() => {
    if (!autoCheckEnabled) {
      setEnabledAnswerFormatModes((prev) => prev.filter((m) => m === 'speech'))
    }
  }, [autoCheckEnabled])

  const derivedActiveSkills = useMemo(
    () =>
      deriveActiveSkillsFromLessonTestSelection(
        enabledPromptTypes as string[],
        granularEnabledAnswerTypes,
      ) as TestSkillGoal[],
    [enabledPromptTypes, granularEnabledAnswerTypes],
  )

  return {
    enabledPromptTypes,
    setEnabledPromptTypes,
    togglePromptType,
    enabledAnswerFormatModes,
    setEnabledAnswerFormatModes,
    autoCheckEnabled,
    setAutoCheckEnabled,
    compatibleAnswerTypes,
    granularEnabledAnswerTypes,
    derivedActiveSkills,
    tagIds,
    setTagIds,
    attributeConditions,
    setAttributeConditions,
    relaxFilters,
    setRelaxFilters,
    mode,
    setMode,
    countNeed,
    setCountNeed,
    durationMin,
    setDurationMin,
    seriesTarget,
    setSeriesTarget,
    allowHints,
    setAllowHints,
    allowTranscription,
    setAllowTranscription,
    excludeDuplicateWords,
    setExcludeDuplicateWords,
  }
}
